import { z } from 'zod';
import { EMPLOYMENT_TYPE_OPTIONS } from '../types';
import {
  isPersonalNumber,
  isValidPhoneNumberInput,
} from '../../../../../../utils/validation';
import { normalizeSocialSecurityNumber } from './socialSecurityNumber';
import { ApplicantRole } from '../../../../../../types/PartyApplication/partyApplication';

export const MAX_TERMINATED_EMPLOYEES = 300;

export const SSN_PATTERN = /^(\d{2})?\d{6}[-+]?\d{4}$/;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const employmentTypeValues = EMPLOYMENT_TYPE_OPTIONS.map((option) =>
  Number(option.value)
);

export type TerminatedEmployeesValidationMessages = {
  dateFormat: string;
  ssnLength: string;
  ssnInvalid: string;
  firstNameRequired: string;
  lastNameRequired: string;
  employmentTypeRequired: string;
  startDateRequired: string;
  terminationDateRequired: string;
  endDateRequired: string;
  emailInvalid: string;
  phoneInvalid: string;
  terminationBeforeStart: string;
  endBeforeTermination: string;
  endBeforeStart: string;
  minEmployees: string;
  maxEmployees: string;
  duplicateSsn: string;
  duplicateEmail: string;
  duplicatePhone: string;
};

const defaultValidationMessages: TerminatedEmployeesValidationMessages = {
  dateFormat: 'Ange datum som ÅÅÅÅ-MM-DD',
  ssnLength: 'Personnummer måste vara 12 tecken',
  ssnInvalid: 'Ogiltigt personnummer',
  firstNameRequired: 'Ange förnamn',
  lastNameRequired: 'Ange efternamn',
  employmentTypeRequired: 'Välj anställningsform',
  startDateRequired: 'Ange startdatum',
  terminationDateRequired: 'Ange uppsägningsdatum',
  endDateRequired: 'Ange slutdatum',
  emailInvalid: 'Ange en giltig e-postadress',
  phoneInvalid: 'Ogiltigt telefonnummer',
  terminationBeforeStart: 'Uppsägningsdatum kan inte vara före startdatum',
  endBeforeTermination: 'Slutdatum kan inte vara före uppsägningsdatum',
  endBeforeStart: 'Slutdatum kan inte vara före startdatum',
  minEmployees: 'Lägg till minst en uppsagd person',
  maxEmployees: `Högst ${MAX_TERMINATED_EMPLOYEES} personer kan registreras per ansökan. Om ni har fler, skicka in en till ansökan.`,
  duplicateSsn: 'Personnumret förekommer flera gånger i listan',
  duplicateEmail: 'E-postadressen förekommer flera gånger i listan',
  duplicatePhone: 'Telefonnumret förekommer flera gånger i listan',
};

const dateSchema = (
  requiredMessage: string,
  dateFormatMessage: string,
  applicantRole?: ApplicantRole
) => {
  const roleNumber =
    typeof applicantRole === 'string' ? Number(applicantRole) : applicantRole;
  return roleNumber === ApplicantRole.companyRepresentative
    ? requiredDate(requiredMessage, dateFormatMessage)
    : optionalDate(dateFormatMessage);
};

const requiredDate = (missingMessage: string, dateFormatMessage: string) =>
  z
    .string()
    .min(1, missingMessage)
    .pipe(z.string().regex(ISO_DATE_PATTERN, dateFormatMessage));

const optionalDate = (dateFormatMessage: string) =>
  z
    .string()
    .optional()
    .refine((value) => !value || ISO_DATE_PATTERN.test(value), {
      message: dateFormatMessage,
    });

export const isBefore = (first: string, second: string): boolean =>
  ISO_DATE_PATTERN.test(first) && ISO_DATE_PATTERN.test(second)
    ? first < second
    : false;

export const buildTerminatedEmployeesSchema = (
  applicantRole?: ApplicantRole,
  messages?: Partial<TerminatedEmployeesValidationMessages>
) => {
  const resolvedMessages: TerminatedEmployeesValidationMessages = {
    ...defaultValidationMessages,
    ...messages,
  };

  const terminatedEmployeeSchema = z
    .object({
      socialSecurityNumber: z
        .string()
        .refine((value) => normalizeSocialSecurityNumber(value).length === 12, {
          message: resolvedMessages.ssnLength,
        })
        .refine(
          (value) => isPersonalNumber(normalizeSocialSecurityNumber(value)),
          {
            message: resolvedMessages.ssnInvalid,
          }
        ),
      firstName: z.string(),
      lastName: z.string(),
      employmentType: z.coerce
        .number()
        .refine((value) => employmentTypeValues.includes(value), {
          message: resolvedMessages.employmentTypeRequired,
        }),
      startDate: dateSchema(
        resolvedMessages.startDateRequired,
        resolvedMessages.dateFormat,
        applicantRole
      ),

      terminationDate: dateSchema(
        resolvedMessages.terminationDateRequired,
        resolvedMessages.dateFormat,
        applicantRole
      ),

      endDate: dateSchema(
        resolvedMessages.endDateRequired,
        resolvedMessages.dateFormat,
        applicantRole
      ),
      email: z
        .string()
        .refine(
          (value) =>
            value.trim().length === 0 ||
            z.string().email().safeParse(value).success,
          { message: resolvedMessages.emailInvalid }
        )
        .optional(),
      phone: z
        .string()
        .refine(
          (value) =>
            value.trim().length === 0 || isValidPhoneNumberInput(value, 10),
          {
            message: resolvedMessages.phoneInvalid,
          }
        )
        .optional(),
    })
    .superRefine((data, ctx) => {
      if (isBefore(data.terminationDate ?? '', data.startDate ?? '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: resolvedMessages.terminationBeforeStart,
          path: ['terminationDate'],
        });
      }
      if (isBefore(data.endDate ?? '', data.terminationDate ?? '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: resolvedMessages.endBeforeTermination,
          path: ['endDate'],
        });
      }
      if (isBefore(data.endDate ?? '', data.startDate ?? '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: resolvedMessages.endBeforeStart,
          path: ['endDate'],
        });
      }
    });

  return z
    .array(terminatedEmployeeSchema)
    .min(1, resolvedMessages.minEmployees)
    .max(MAX_TERMINATED_EMPLOYEES, resolvedMessages.maxEmployees)
    .superRefine((employees, ctx) => {
      const ssnIndexes = new Map<string, number[]>();
      const emailIndexes = new Map<string, number[]>();
      const telephoneIndexes = new Map<string, number[]>();

      employees.forEach((employee, index) => {
        const ssnKey = normalizeSocialSecurityNumber(
          employee.socialSecurityNumber
        );
        const emailKey = (employee.email ?? '').trim().toLowerCase();
        const telephoneKey = (employee.phone ?? '').trim();

        if (ssnKey) {
          const ssnIndexesForKey = ssnIndexes.get(ssnKey);
          if (ssnIndexesForKey) {
            ssnIndexesForKey.push(index);
          } else {
            ssnIndexes.set(ssnKey, [index]);
          }
        }

        if (emailKey) {
          const emailIndexesForKey = emailIndexes.get(emailKey);
          if (emailIndexesForKey) {
            emailIndexesForKey.push(index);
          } else {
            emailIndexes.set(emailKey, [index]);
          }
        }

        if (telephoneKey) {
          const telephoneIndexesForKey = telephoneIndexes.get(telephoneKey);
          if (telephoneIndexesForKey) {
            telephoneIndexesForKey.push(index);
          } else {
            telephoneIndexes.set(telephoneKey, [index]);
          }
        }
      });

      ssnIndexes.forEach((indexes) => {
        if (indexes.length < 2) {
          return;
        }

        indexes.forEach((index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: resolvedMessages.duplicateSsn,
            path: [index, 'socialSecurityNumber'],
          });
        });
      });

      emailIndexes.forEach((indexes) => {
        if (indexes.length < 2) {
          return;
        }

        indexes.forEach((index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: resolvedMessages.duplicateEmail,
            path: [index, 'email'],
          });
        });
      });

      telephoneIndexes.forEach((indexes) => {
        if (indexes.length < 2) {
          return;
        }

        indexes.forEach((index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: resolvedMessages.duplicatePhone,
            path: [index, 'phone'],
          });
        });
      });
    });
};
